import { ICommandBarItemProps } from "@fluentui/react"
import { Bus } from "baconjs"
import { merge } from "lodash"

import { Http } from "@encoway/c-services-js-client"
import { L10n } from "@encoway/l10n"

import {
    ReadyState,
    NodeMessageType,
    VisualizationOptions
} from "../../constants"
import { generateAR } from "./ar"
import { generateDXF } from "./dxf"
import { generateIFC } from "./ifc"
import { determineRenderSettings, render } from "./render"
import { generateSTEP } from "./step"
import {
    IImages,
    IVisualizationCloud,
    IVisualizationEditorSettings,
    IVisualizationOption,
    IVisualizationServer,
    IVisualizationSettings
} from "./visualizationService.types"

export class VisualizationService {
    static visualization: IVisualizationServer
    static eventBus: Bus<any>

    /**
     * Initializes VisualizationService with the visualization API instance.
     * @param {IVisualizationServer} visualization The visualization API.
     * @param {Bus} eventBus The bacon bus to communicate with the visualization component.
     */
    static init(visualization: IVisualizationServer, eventBus: Bus<any>) {
        VisualizationService.visualization = visualization
        VisualizationService.eventBus = eventBus
    }

    /**
     * Determines render settings from the characteristic "encoway.renderSettings" of the product.
     *
     * @param {String} productId The ID of the product with render settings.
     * @param {Object} visSettings Visualization settings.
     * @return {Object | undefined} The product render settings or undefined, if no settings characteristic is defined
     */
    static async determineRenderSettings(
        productId: string,
        visSettings: IVisualizationEditorSettings
    ): Promise<object | undefined> {
        return determineRenderSettings(
            VisualizationService.visualization,
            productId,
            visSettings
        )
    }

    /**
     * Renders images of the scene.
     *
     * @param {Object} settings The settings for the rendering.
     * @return {IImages | undefined} The object with the scene views as keys and corresponding base64 encoded images as values.
     */
    static async render(settings: object): Promise<IImages | undefined> {
        return render(VisualizationService.visualization, settings)
    }

    /**
     * Uses AR Service to upload the 3D data of the configuration result and provides a link to the AR viewer as a QR code.
     *
     * @param {IVisualizationOption} option The AR settings.
     * @param {String} productId The ID of the product with the possible AR settings.
     */
    static generateAR(option: IVisualizationOption, productId: string): void {
        generateAR(
            VisualizationService.visualization,
            option,
            VisualizationService.eventBus,
            productId
        )
    }

    /**
     * Exports stencil patterns from the configuration result in the form of 2D drawings (DXF format).
     * @param {IVisualizationOption} option The DXF settings.
     * @param {String} productId The ID of the product to get the stencil patterns from.
     */
    static generateDXF(option: IVisualizationOption, productId: string): void {
        generateDXF(
            VisualizationService.visualization,
            option,
            VisualizationService.eventBus,
            productId
        )
    }

    /**
     * Gathers metadata and 3D data of the configuration result and exports them to the IFC format.
     * @param {IVisualizationOption} option The IFC settings.
     * @param {String} productId The ID of the product to get the IFC template from.
     */
    static generateIFC(option: IVisualizationOption, productId: string): void {
        generateIFC(
            VisualizationService.visualization,
            option,
            VisualizationService.eventBus,
            productId
        )
    }

    /**
     * Extracts the result of the configuration process and exports it in the STEP format.
     * @param {IVisualizationOption} option The STEP settings.
     */
    static generateSTEP(option: IVisualizationOption): void {
        generateSTEP(
            VisualizationService.visualization,
            option,
            VisualizationService.eventBus
        )
    }

    /**
     * Extends visualization UI settings with the corresponding header items, if the visualization options are provided in the settings.
     * @param {String} productId The ID of the configuration product.
     * @param {IVisualizationSettings} visSettings The visualization settings.
     */
    static setupVisualizationOptions(
        productId: string,
        visSettings: IVisualizationSettings
    ) {
        const settings = visSettings.settings
        const options = visSettings.options ?? []
        if (!Array.isArray(options) || options.length === 0) {
            return
        }
        const optionsItem: ICommandBarItemProps = {
            key: "vis-options",
            text: L10n.format(
                "Application.Visualization.Header.HeaderMenu.exportscene"
            ),
            iconProps: {
                iconName: "Download"
            },
            subMenuProps: {
                items: options.map((option) => ({
                    key: option.name,
                    text: L10n.format(
                        `Application.Visualization.Header.HeaderMenu.${option.name}`
                    ),
                    onClick: () =>
                        VisualizationOptionsStrategy[option.name](
                            option,
                            productId
                        )
                }))
            }
        }

        const headerItems = settings?.ui?.header?.items ?? []
        headerItems.unshift(optionsItem)

        merge(settings, {
            ui: {
                components: {
                    header: "north"
                },
                header: {
                    items: headerItems,
                    replace:
                        !settings?.ui?.components?.header ||
                        settings?.ui?.header?.replace
                }
            }
        })
    }

    /**
     * Verifies whether a connection to the api can be established.
     * @param http to use
     * @param baseUrl of the CPQ sales
     * @returns {Promise<void | never>} Http response
     */
    static async verifyConnection(
        http: Http,
        baseUrl: string
    ): Promise<void | never> {
        return http
            .fetch(`${baseUrl}/api/visualization/status`, {})
            .then((response) => {
                if (response.ok) {
                    console.log("Everything is OK :)")
                }
            })
            .catch((error) => console.error(error))
    }

    /**
     * Loads the current visualization state.
     * @param http to use
     * @param baseUrl of the CPQ sales
     * @param lineItem id of the lineItem that provides the saved data
     * @param sdmi id of the salesdocument managed instance that contains the lineItem
     * @returns {Promise<any>} The visualization state as promise
     */
    static async loadAssemblyState(
        http: Http,
        baseUrl: string,
        lineItem: string,
        sdmi: string
    ): Promise<any> {
        const fetchParams = {
            method: "GET",
            headers: new Headers({
                Accept: "application/json"
            })
        }

        return http
            .fetch(
                `${baseUrl}/api/visualization/${sdmi}/${lineItem}`,
                fetchParams
            )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                }
                console.error("Failed to retrieve saved state!", response)
            })
    }

    /**
     * Determines the state of the configuration(s) in the scene. The state can be one of the following:
     *   CONFLICT - At least one node in the scene has an error message set (e.g. due to overfilled anchors)
     *   NOT_READY - The state is not CONFLICT and at least one configuration is not completed
     *   READY - The state is neither CONFLICT nor NOT_READY
     *
     * @returns {string} The ready state of the assembly
     */
    static determineConfigurationReadyState(
        cloud?: IVisualizationCloud
    ): string {
        const nodes = cloud?.graph().nodes()

        // Check if the scene is empty. If so, not ready state is returned
        if (!nodes || nodes.length === 0) {
            return ReadyState.NotReady
        }

        // Check if an error message is set on any node. If so, conflict state is returned
        const errorSetOnAnyNode = nodes.some((node) =>
            node.messages.some(
                (message) => message.type === NodeMessageType.Error
            )
        )
        if (errorSetOnAnyNode) {
            return ReadyState.Conflict
        }

        // Check if all configurations are ready. If not, not ready state is returned
        const allConfigurationsAreReady = cloud?.getConfigurationStatus()
        if (!allConfigurationsAreReady) {
            return ReadyState.NotReady
        }

        // There are products in the scene, no error messages are set and all configurations are ready, so return ready state
        return ReadyState.Ready
    }
}

export const VisualizationOptionsStrategy = {
    [VisualizationOptions.AR]: VisualizationService.generateAR,
    [VisualizationOptions.DXF]: VisualizationService.generateDXF,
    [VisualizationOptions.IFC]: VisualizationService.generateIFC,
    [VisualizationOptions.STEP]: VisualizationService.generateSTEP
}
